import { Shimmer } from '@components'
import { IDashboardInvoiceSummary } from '@query'
import { formatCurrency } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useDashboardOverview } from '../hooks/useDashboardOverview'
import { Card } from './shared/Card'

export const InvoicesCard: React.FC = () => {
  const [t] = useTranslation()
  const { data: overview, isLoading } = useDashboardOverview()
  const sales = overview?.invoices
  const purchases = overview?.purchase_invoices

  if (!isLoading && !sales && !purchases) return null

  return (
    <Card
      title={t('dashboard.invoices.title')}
      tooltip={t('dashboard.invoices.tooltip')}
      ask={{
        prompts: t('dashboard.invoices.ask.prompts', { returnObjects: true }) as string[]
      }}
    >
      <Body>
        <InvoiceGroup
          heading={t('dashboard.invoices.salesTitle')}
          summary={sales}
          isLoading={isLoading}
          rowPrefix="dashboard.invoices.row"
        />
        <InvoiceGroup
          heading={t('dashboard.invoices.purchaseTitle')}
          summary={purchases}
          isLoading={isLoading}
          rowPrefix="dashboard.invoices.purchaseRow"
        />
      </Body>
    </Card>
  )
}

const InvoiceGroup: React.FC<{
  heading?: string
  summary?: IDashboardInvoiceSummary | null
  isLoading: boolean
  rowPrefix: string
}> = ({ heading, summary, isLoading, rowPrefix }) => {
  const [t] = useTranslation()
  const rows = [
    { key: 'overdue', tone: 'strong' as const, amount: summary?.overdue.amount ?? 0 },
    { key: 'awaiting', tone: 'mid' as const, amount: summary?.awaiting.amount ?? 0 }
  ]

  return (
    <Group>
      {heading && <GroupTitle>{heading}</GroupTitle>}
      {rows.map(row => (
        <Row key={row.key}>
          <Dot $tone={row.tone} />
          <Label>{t(`${rowPrefix}.${row.key}`)}</Label>
          {isLoading ? (
            <Shimmer width="5rem" height="0.9375rem" />
          ) : (
            <Value>{formatCurrency(row.amount / 100)}</Value>
          )}
        </Row>
      ))}
    </Group>
  )
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem
    ${({ theme }) => theme.spacing.lg}rem;
`

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const GroupTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.iconGray};
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const Dot = styled.span<{ $tone: 'strong' | 'mid' }>`
  flex-shrink: 0;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ theme, $tone }) =>
    $tone === 'strong' ? theme.colors.primary : theme.dashboard.infoMid};
`

const Label = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Value = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.menuBg};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`
